/** Controller for account-level actions: delete account.
 *
 * Layer: `controller` per docs/design/spa-account-extraction-260524.md §3.3.
 *
 * Covers:
 *   - deleteAccount()     — double-confirm, POST /api/account/delete, signOut,
 *                           redirect to /
 *   - attach()            — delegated click handler for data-action delete-account
 *
 * Self-serve purchase (buy-tokens / buy-tokens-custom) was removed with the
 * Stripe strike (invoice-everywhere Beta;
 * beta-billing-purdue-unlimited-no-dollars-260821 Phase 6).
 *
 * Delegated pattern: `document.addEventListener('click')` + `e.target.closest('[data-action]')`.
 *
 * WHY _sbClient is lazy: Supabase may not be initialised when the controller
 * is constructed (script load order). We resolve it inside deleteAccount()
 * using `window.adatool?.supabase?.getClient()`.
 *
 * Extracted from web/static/account.html lines 721-771 in Phase AC.5.
 */
// WHY: app-injected globals live under window.adatool.* (2026-05-25 namespace migration).
// Accessed via window.adatool.supabase / .log.
export class AccountActionsController {
    _authFetch;
    _doc;
    _fetch;
    _localStorage;
    _location;
    constructor(deps) {
        this._authFetch = deps.authFetch;
        this._doc = deps.doc;
        this._fetch = deps.fetch;
        this._localStorage = deps.localStorage;
        this._location = deps.location;
    }
    /** Bind the delegated click handler for delete-account. */
    attach() {
        this._doc.addEventListener('click', async (e) => {
            const btn = e.target?.closest('[data-action]');
            if (!btn)
                return;
            const action = btn.dataset['action'];
            if (action === 'delete-account') {
                await this.deleteAccount();
            }
        });
    }
    async deleteAccount() {
        if (!confirm('Permanently delete your account? This cannot be undone.\n\n'
            + '• Your individual subscription will be cancelled\n'
            + '• You will be removed from any group\n'
            + '• Files will be cleaned up within 30 days'))
            return;
        if (!confirm('Are you absolutely sure? This is permanent.'))
            return;
        try {
            const resp = await this._authFetch('/api/account/delete', { method: 'POST' });
            const data = await resp.json().catch(() => ({}));
            if (resp.ok) {
                alert('Account deleted successfully.');
                // WHY lazy: _sbClient resolved here, not at construction time,
                // because Supabase initialisation happens asynchronously.
                const sbClient = window.adatool?.supabase?.getClient() ?? null;
                try {
                    if (sbClient)
                        await sbClient.auth.signOut({ scope: 'global' });
                }
                catch (e) {
                    window.adatool?.log?.warn('[account] Supabase signOut failed:', e);
                }
                try {
                    await this._fetch('/api/auth/logout', { method: 'POST' });
                }
                catch (_e) { /* best-effort */ }
                // Collect keys first — removeItem mutates indices during iteration.
                const sbKeys = [];
                for (let i = 0; i < this._localStorage.length; i++) {
                    const k = this._localStorage.key(i);
                    if (k && k.startsWith('sb-'))
                        sbKeys.push(k);
                }
                sbKeys.forEach(k => this._localStorage.removeItem(k));
                this._location.href = '/';
            }
            else {
                alert(data.detail || 'Failed to delete account. Please try again.');
            }
        }
        catch (_e) {
            alert('Network error. Please try again.');
        }
    }
}
